import React, { useEffect, useState } from "react";
import { Divider } from "@mui/material";
import { ILanguage } from "modules/localization/models";
import { getTranslatedBody, getTranslatedTitle } from "utils/getTranslatedContent";
import { parseAnchors } from "utils/htmlUtils";
import { CustomCss } from "modules/common/components/customCss";
import HeroBanner from "../../heroBanner";
import Comments from "modules/posts/components/post-view/comments/comments";
import { IContentOverview, ContentType } from "modules/common/models";
import { connect, ConnectedProps } from "react-redux";
import { GlobalApplicationState } from "globalApplicationState";
import FileAttachmentDisplay from "modules/settings/components/content/fileAttachmentDisplay";
import { generateBody } from "utils/contentUtils";

import './contentView.sass';

interface IContentViewProps {
    contentType: ContentType;
    content: IContentOverview;
    heroBannerChildren?: React.ReactNode;
    showComments?: boolean; // if post/event has comments, do we display them?
    onChangeActiveLcid?: (activeLcid: string) => void;
    typeBannerComponent?: React.ReactNode;
    pageContentComponent?: React.ReactNode;
    bottomPageContentComponent?: React.ReactNode;
    featureBannerComponent?: React.ReactNode;
    pageContentInfoComponent?: React.ReactNode;
    defaultLCID?: string;
    preview?: boolean;
    shareButtonComponent?: React.ReactNode;
    shareScreenComponent?: React.ReactNode;
    contentRef?: React.RefObject<HTMLDivElement>;
    contentViewContainerStyle?: React.CSSProperties;
}

const ContentView: React.FunctionComponent<PropsWithRedux> = ({
    content,
    lcidMappings,
    showComments = true,
    tenantSettings,
    contentType,
    heroBannerChildren,
    onChangeActiveLcid,
    typeBannerComponent,
    pageContentComponent,
    bottomPageContentComponent,
    featureBannerComponent,
    pageContentInfoComponent,
    defaultLCID,
    preview,
    shareButtonComponent,
    shareScreenComponent,
    contentRef,
    contentViewContainerStyle
}) => {
    const [activeLcid, setActiveLcid] = useState<string>(defaultLCID ?? tenantSettings.defaultLCID);
    const [availableLanguages, setAvailableLanguages] = useState<Partial<ILanguage>[]>();

    useEffect(() => {
            const availableLcids: string[] = Object.keys(content.translatedContent!);
            const availableLanguages: Partial<ILanguage>[] = availableLcids.map((lcid) => ({
                language: lcidMappings[lcid]
                    ? lcidMappings[lcid].languageShort
                    : lcid,
                lcid
            }));
            const activeLcid: string = content.translatedContent![content.preferredLCID]
                ? content.preferredLCID
                : tenantSettings.defaultLCID;

            setAvailableLanguages(availableLanguages);
            setActiveLcid(activeLcid);
            if (onChangeActiveLcid) onChangeActiveLcid(activeLcid);
        }, 
        [content.translatedContent, tenantSettings.defaultLCID, content.preferredLCID, lcidMappings]
    );

    const renderLanguages = (): JSX.Element => {
        if (!availableLanguages || availableLanguages.length < 2)
            return <></>;

        return (
            <div id="available-languages-list" className="available-languages">
                <span className="emphasis">Available In: </span>
                {availableLanguages
                    .map<React.ReactNode>(({ language, lcid = tenantSettings.defaultLCID }, i) =>
                        <span
                            key={lcid}
                            onClick={() => {
                                setActiveLcid(lcid); 
                                onChangeActiveLcid && onChangeActiveLcid(lcid);
                            }}
                            className={lcid === activeLcid ? "" : "inactive-language"}
                            id={`available-in-lang-${i}`}
                        >
                            {language}
                        </span>
                    )
                    .reduce((prev, curr) => [prev, ", ", curr])
                }
            </div>
        );
    }

    const title = getTranslatedTitle(content.translatedContent, activeLcid);

    return <>
        <CustomCss enabled={tenantSettings.showFeatures.tinyMceCustomCssEnabled} />
        <div className="content-view">
            {shareScreenComponent}
            <HeroBanner 
                useCanvas={content.status === "draft"} 
                bannerColor={content.bannerColor} 
                image={content.image} 
                imageUrl={content.imageUrl} 
                attachedContent={content.attachedContent}
            >
                {heroBannerChildren}
            </HeroBanner>
            {typeBannerComponent}
            <div style={{ backgroundColor: "#ffffff" }}>
                <div id="content-view" className="page-content" style={{ paddingTop: '10px' }}>
                    <div id="content-title" className="title">
                        {title}
                        {shareButtonComponent}
                    </div>
                    {pageContentInfoComponent}
                    {featureBannerComponent}
                    <div className="author">
                        <Divider light />
                        <div className="author-details">
                            <div 
                                className="author-avatar" 
                                style={{ backgroundColor: content.author?.avatar?.color ?? content.author?.avatarColor ?? "" }}
                            >
                                {content.author?.name ? content.author.name[0] : ""}
                            </div>
                            <div>
                                <div>{contentType === ContentType.Post ? "Post" : "Organized"} By</div>
                                {
                                    content && content.author && content.author.email
                                        ? <a href={`mailto:${content.author.email}`} className="author-name">{content.author.name}</a>
                                        : <div className="author-name"> {content && content.author ? content.author.name : ""}</div>
                                }
                            </div>
                        </div>
                        <Divider light />
                    </div>
                    {renderLanguages()}
                    <div className="body">
                        {generateBody(parseAnchors(getTranslatedBody(content.translatedContent, activeLcid)), "view-content")}
                    </div>
                    {
                        content.fileAttachments && content.fileAttachments.length > 0 &&
                        <FileAttachmentDisplay 
                            attachments={content.fileAttachments} 
                            titleStyle={{
                                fontSize: 14,
                                fontWeight: 500,
                                marginBottom: 5
                            }}
                            fileDisplayStyle={{ width: 'fit-content' }}
                            documentTypeName="Document"
                            displayAttachmentsInRows
                        />
                    }
                    {pageContentComponent}
                </div>
                {bottomPageContentComponent}
                {
                    preview && showComments &&
                    <div style={{ padding: "20px" }}>
                        <Comments
                            comments={[]}
                            isSubscribed={false}
                            onUpdateComments={() => { }}
                            onUpdateCommentSubscriptions={() => { }}
                            preview
                            commentsEnabledOnPost={content.commentingEnabled}
                            commentsEnabledOnTenant={tenantSettings.commentsEnabled}
                            id={content.id}
                        />
                    </div>
                }
            </div>
        </div>
    </>;
};

const connector = connect(
    (state: GlobalApplicationState, ownProps: IContentViewProps) => ({
        ...ownProps,
        lcidMappings: state.resources.lcidMappings,
        tenantSettings: state.settings.tenantSettings,
    }),
    {
    }
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(ContentView);